import React, { useCallback, useEffect, useState } from 'react';
import { Spin } from 'antd';
import { BlueButton } from '@share/components';
import { useSelector } from 'react-redux';
import { ILoginState } from '@share/store/slices';
import { axiosInstanceB2B, formatDate, Toaster } from '@share/utils';
import { Urls } from '@share/constants';
import { useMergeLink } from '@mergeapi/react-merge-link';
import { FormattedMessage, IntlShape } from 'react-intl';
import { getHrItems } from "../../../_mock-api's";
import './style.scss';
interface IMapStateToProps {
  loginStore: ILoginState;
}
interface LinkedAccount {
  integration: string;
  createdDate: string;
}

interface LinkedAccountsState {
  integrations: LinkedAccount[];
  isLoading: boolean;
}

const HRIntegrations: React.FC<{ intl: IntlShape }> = ({ intl }) => {
  const loginStore = useSelector((state: IMapStateToProps) => state.loginStore);
  const { account } = loginStore;
  const [loading, setLoading] = useState(true);
  const [publicToken, setPublicToken] = useState<string>('');
  const [linkToken, setLinkToken] = useState<{ linkToken: string; isLoading: boolean , integration: string }>({
    linkToken: '',
    isLoading: false,
    integration: '',
  });
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccountsState>({
    integrations: [],
    isLoading: true,
  });
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});

  const fetchExistingAccount = async (loading?: string) => {
    try {
      if (loading) {
      setLoadingStates((prev) => ({ ...prev, [loading]: true }));
      }
      const result = await axiosInstanceB2B.get(`${Urls.MergeLinkedAccounts}/${account?.id}`);
      if (result.status === 200) {
        setLinkedAccounts((prev) => ({ ...prev, isLoading: true }));
        const data = result?.data?.data;
        data.map((item: any) => {
          setLinkedAccounts((prev) => ({
            ...prev,
            integrations: [
              ...prev.integrations,
              {
                integration: item?.integration,
                createdDate: formatDate(item?.createdDate),
              },
            ],
          }));
        });
        setLinkedAccounts((prev) => ({ ...prev, isLoading: false }));
        if (loading) {
          setLoadingStates((prev) => ({ ...prev, [loading]: false }));
        }
      } else {
        if (loading) {
          setLoadingStates((prev) => ({ ...prev, [loading]: false }));
        }
        setLinkedAccounts((prev) => ({ ...prev, isLoading: false }));
        Toaster.info('Failed to retrieve link token. Please try again later.');
      }
    } catch (error) {
      if (loading) {
        setLoadingStates((prev) => ({ ...prev, [loading]: false }));
      }
      setLinkedAccounts((prev) => ({ ...prev, isLoading: false }));
      console.error('Error fetching account:', error);
      alert('Failed to retrieve account. Please try again later.');
    }
  };

  useEffect(() => {
    setLoading(false);
    fetchExistingAccount();
  }, []);

  const fetchLinkToken = async (href: string) => {
    try {
      setLinkToken((prev) => ({ ...prev, isLoading: true, integration: href }));
      setLoadingStates((prev) => ({ ...prev, [href]: true }));
      const payload = {
        originId: `${account?.id}-${href}`,
        organizationEmail: account?.email,
        organizationName: account?.name,
        ...(href && { integration: href }),
      };
      const result = await axiosInstanceB2B.post(`${Urls.MergeLinktoken}`, payload);
      if (result.status === 200) {
        setLinkToken((prev) => ({ ...prev, linkToken: result.data.link_token, isLoading: false }));
        setLoadingStates((prev) => ({ ...prev, [href]: false }));
      } else {
        Toaster.info('Failed to retrieve link token. Please try again later.');
        setLinkToken((prev) => ({ ...prev, isLoading: false }));
        setLoadingStates((prev) => ({ ...prev, [href]: false }));
      }
    } catch (error) {
      setLinkToken((prev) => ({ ...prev, isLoading: false }));
      setLoadingStates((prev) => ({ ...prev, [href]: false }));
      console.error('Error fetching link token:', error);
      alert('Failed to retrieve link token. Please try again later.');
    }
  };

  const fetchCompanyEmployee = async () => {
    try {
      setLoading(true);
      const response = await axiosInstanceB2B.get(`${Urls.GetCompanyEmployees}/${account?.id}`);
      if (response?.status === 200 && response?.data?.results) {
        const formattedData = response?.data?.results.map((employee: any) => {
          return {
            ...employee,
            date_of_birth: formatDate(employee.date_of_birth),
          };
        });
        // setEmployeeList(formattedData);
        setLoading(false);
      } else if (response?.data?.status === 400) {
        Toaster.info('Please add your account or contact TravBuisness');
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error('Error fetching employee data:', error);
      Toaster.info('Failed to fetch employee data. Please try again later.');
    }
  };
  const onSuccess = useCallback(async (public_token: string) => {
    try {
      const payload = {
        AccountId: account?.id,
        publicToken: public_token,
        integration: linkToken.integration,
      };
      const result = await axiosInstanceB2B.post(Urls.MergeExchangePublicToken, payload);
      if (result?.data?.status === 200) {
        setPublicToken(public_token);
        fetchExistingAccount(linkToken.integration);
      } else {
        Toaster.info('Failed to retrieve account token. Please try again later.');
        // setIsEmployeeListLoading(false);
      }
    } catch (error) {
      console.error('Error fetching account token:', error);
      alert('Failed to retrieve account token. Please try again later.');
    }
  }, [linkToken.integration]);
  const { open, isReady } = useMergeLink({
    linkToken: linkToken.linkToken,
    onExit: () => {
      setLinkToken({ linkToken: '', isLoading: false, integration: '' });
    },
    onSuccess,
  });
  const handleOpen = async (href: string) => {
    await fetchLinkToken(href);
    open();
    if (linkToken?.linkToken && linkToken?.isLoading === false) {
    }
  };
  const hrItems = getHrItems(intl);
  return (
    <>
      <section className="pt-1">
        <div className="container">
          <div className="row">
            {hrItems?.map((items) => {
              const isLinked = linkedAccounts.integrations.some(
                (account) => account.integration === items.href,
              );
              return (
                <div className="card border mb-4 flex-grow-1" key={items.href}>
                  <div className="card-body">
                    <div className="d-md-flex justify-content-md-between align-items-center">
                      <div className="d-flex align-items-center">
                        <img
                          src={items.icon}
                          width="50px"
                          alt={`${items.label} logo`}
                          className="flex-shrink-0 me-3"
                        />
                        <div className="ms-2 pe-2">
                          <h6 className="card-title mb-0">{items.label}</h6>
                          <ul className="nav nav-divider small">
                            <li className="nav-item">{items.description}</li>
                          </ul>
                        </div>
                      </div>
                      <div className="mt-2 mt-md-0 d-flex align-items-center justify-content-end">
                        <BlueButton
                          block
                          type="primary"
                          className="btn btn-primary mw-110"
                          disabled={!isReady}
                          onClick={() => handleOpen(items.href)}
                        >
                          {loadingStates[items.href] || linkedAccounts.isLoading ? (
                            <Spin spinning={loadingStates[items.href]} />
                          ) : isLinked ? (
                            <FormattedMessage id="relink" />
                          ) : (
                            <FormattedMessage id="connect.button" />
                          )}
                        </BlueButton>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default HRIntegrations;
